import { motion } from "framer-motion";
import PlayingCard from "./PlayingCard";
import { Coins } from "lucide-react";

/**
 * Trade Animation Overlay
 * Shows animated cards and chips rotating around center during a successful trade
 * 
 * Flow:
 * 1. Items from both players are positioned (top: offer, bottom: request)
 * 2. Items move to center
 * 3. Items rotate 180 degrees clockwise around center point
 * 4. Items move to their new positions (swapped)
 */
export default function TradeAnimation({ 
  tradeData, 
  onComplete 
}) {
  if (!tradeData) return null;

  const { 
    fromPlayer, 
    toPlayer, 
    offerCards, 
    requestCards, 
    offerChips, 
    requestChips 
  } = tradeData;

  // Animation timing
  const TOTAL_DURATION = 1.8;

  // Auto-complete after animation
  setTimeout(() => {
    onComplete?.();
  }, TOTAL_DURATION * 1000 + 300);

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center">
      {/* Dark overlay */}
      <motion.div 
        className="absolute inset-0 bg-black/70"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      {/* Trade animation container */}
      <div className="relative w-full max-w-md h-64 flex items-center justify-center">
        
        {/* Player labels */}
        <motion.div 
          className="absolute top-0 left-1/2 -translate-x-1/2 text-xs font-medium text-brass bg-card/90 px-2 py-0.5 rounded-full"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {fromPlayer}
        </motion.div>
        <motion.div 
          className="absolute bottom-0 left-1/2 -translate-x-1/2 text-xs font-medium text-green-400 bg-card/90 px-2 py-0.5 rounded-full"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {toPlayer}
        </motion.div>

        {/* Rotating container for the trade items */}
        <motion.div
          className="relative"
          initial={{ rotate: 0 }}
          animate={{ rotate: 180 }}
          transition={{ 
            duration: TOTAL_DURATION * 0.6,
            delay: TOTAL_DURATION * 0.3,
            ease: "easeInOut"
          }}
          style={{ transformOrigin: "center center" }}
        >
          {/* From Player's items (top, moving to center then rotating) */}
          <motion.div 
            className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
            initial={{ y: -80 }}
            animate={{ y: -30 }}
            transition={{ duration: TOTAL_DURATION * 0.3, ease: "easeOut" }}
          >
            {/* Offer Cards */}
            <div className="flex gap-1">
              {offerCards?.map((card, i) => (
                <motion.div 
                  key={`offer-${i}`}
                  initial={{ scale: 1 }}
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: TOTAL_DURATION }}
                >
                  <PlayingCard suit={card.suit} rank={card.rank} isPublic={true} size="sm" />
                </motion.div>
              ))}
            </div>
            {/* Offer Chips */}
            {offerChips > 0 && (
              <div className="flex items-center gap-1 bg-brass/30 px-2 py-0.5 rounded-full">
                <Coins className="w-3 h-3 text-brass" />
                <span className="text-brass text-sm font-bold">{offerChips}</span>
              </div>
            )}
          </motion.div>

          {/* To Player's items (bottom, moving to center then rotating) */}
          <motion.div 
            className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
            initial={{ y: 80 }}
            animate={{ y: 30 }}
            transition={{ duration: TOTAL_DURATION * 0.3, ease: "easeOut" }}
          >
            {/* Request Cards */}
            <div className="flex gap-1">
              {requestCards?.map((card, i) => (
                <motion.div 
                  key={`request-${i}`}
                  initial={{ scale: 1 }}
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: TOTAL_DURATION }}
                >
                  <PlayingCard suit={card.suit} rank={card.rank} isPublic={true} size="sm" />
                </motion.div>
              ))}
            </div>
            {/* Request Chips */}
            {requestChips > 0 && (
              <div className="flex items-center gap-1 bg-green-500/30 px-2 py-0.5 rounded-full">
                <Coins className="w-3 h-3 text-green-400" />
                <span className="text-green-400 text-sm font-bold">{requestChips}</span>
              </div>
            )}
          </motion.div>
        </motion.div>

        {/* Center "Trade Complete" text - appears after rotation */}
        <motion.div 
          className="absolute z-10"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: [0, 0, 1, 1, 0], scale: [0.5, 0.5, 1, 1, 0.8] }}
          transition={{
            duration: TOTAL_DURATION,
            times: [0, 0.4, 0.5, 0.85, 1]
          }}
        >
          <div className="text-xl font-display text-brass bg-card/95 px-4 py-2 rounded-lg border border-brass/50 shadow-lg">
            取引成立！
          </div>
        </motion.div>
      </div>
    </div>
  );
}
